import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, Users, ClipboardList, TrendingUp, Image, X, Download, Bell, Megaphone, Trash2, FileText, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import VideoTutorialButton from '../components/VideoTutorialButton';
import { downloadAllClassBackups } from '../utils/downloadClassBackups';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherDashboard = ({ user, onLogout }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ attendance_percentage: 0, active_tasks: 0 });
  const [encuadreDialog, setEncuadreDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [encuadreMode, setEncuadreMode] = useState('choice'); // choice | manual | view
  const [manualCriterios, setManualCriterios] = useState([]);
  const [critNombre, setCritNombre] = useState('');
  const [critPeso, setCritPeso] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [currentAdminNotifIndex, setCurrentAdminNotifIndex] = useState(0);
  const [jefeNotifications, setJefeNotifications] = useState([]);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    fetchClasses();
    fetchStats();
    fetchAdminNotifications();
    fetchJefeNotifications();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAdminNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/notifications-admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
    }
  };

  const fetchJefeNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/notifications-jefe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJefeNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching jefe notifications:', error);
    }
  };

  const dismissJefeNotif = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications-jefe/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJefeNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const markAdminNotifRead = async () => {
    const notif = adminNotifications[currentAdminNotifIndex];
    if (!notif) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications-admin/${notif.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (currentAdminNotifIndex < adminNotifications.length - 1) {
        setCurrentAdminNotifIndex(prev => prev + 1);
      } else {
        setAdminNotifications([]);
        setCurrentAdminNotifIndex(0);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const downloadClassExcel = async (e, classItem) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/classes/${classItem.id}/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${classItem.subject}_${classItem.group}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error al descargar el reporte');
    }
  };

  const handleEncuadreClick = (e, classItem) => {
    e.stopPropagation();
    setSelectedClass(classItem);
    setCritNombre('');
    setCritPeso('');
    if (classItem.encuadre_url) {
      setEncuadreMode('view');
    } else if (classItem.encuadre_manual && classItem.encuadre_manual.length > 0) {
      setManualCriterios(classItem.encuadre_manual.map(c => ({ nombre: c.nombre, peso: c.peso })));
      setEncuadreMode('manual');
    } else {
      setManualCriterios([]);
      setEncuadreMode('choice');
    }
    setEncuadreDialog(true);
  };

  const chooseManual = () => {
    if (selectedClass?.encuadre_manual?.length) {
      setManualCriterios(selectedClass.encuadre_manual.map(c => ({ nombre: c.nombre, peso: c.peso })));
    } else {
      setManualCriterios([]);
    }
    setEncuadreMode('manual');
  };

  const addCriterio = () => {
    const nombre = critNombre.trim();
    if (!nombre) { toast.error('Escribe el nombre del criterio'); return; }
    setManualCriterios(prev => [...prev, { nombre, peso: critPeso.trim() }]);
    setCritNombre('');
    setCritPeso('');
  };

  const removeCriterio = (idx) => {
    setManualCriterios(prev => prev.filter((_, i) => i !== idx));
  };

  const saveManualEncuadre = async () => {
    if (!selectedClass?.id) return;
    if (manualCriterios.length === 0) { toast.error('Agrega al menos un criterio'); return; }
    const total = manualCriterios.reduce((s, c) => s + (parseFloat(String(c.peso).replace('%', '')) || 0), 0);
    if (total !== 100) {
      if (!window.confirm(`La suma de los pesos es ${total}%, no es 100%. ¿Deseas guardar de todos modos?`)) return;
    }
    setSavingManual(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/classes/${selectedClass.id}/encuadre-manual`, { criterios: manualCriterios }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Encuadre guardado');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el encuadre');
    } finally {
      setSavingManual(false);
    }
  };

  const deleteManualEncuadre = async () => {
    if (!selectedClass?.id) return;
    if (!window.confirm('¿Eliminar el encuadre manual?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/classes/${selectedClass.id}/encuadre-manual`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Encuadre eliminado');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error('Error al eliminar el encuadre');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes (PNG, JPG, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe superar 5MB');
      return;
    }

    await uploadEncuadre(file, selectedClass?.id);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadEncuadre = async (file, classId) => {
    if (!classId) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/classes/${classId}/encuadre`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Encuadre subido exitosamente');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir encuadre');
    } finally {
      setUploading(false);
    }
  };

  const handleReplaceEncuadre = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteEncuadre = async () => {
    if (!selectedClass?.id) return;
    if (!window.confirm('¿Eliminar la foto del encuadre?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/classes/${selectedClass.id}/encuadre`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Encuadre eliminado');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error('Error al eliminar encuadre');
    }
  };

  const handleDownloadAllBackups = async () => {
    if (classes.length === 0) { toast.error('No hay clases para descargar'); return; }
    if (!window.confirm(`Se descargarán ${classes.length} archivo(s), uno por cada clase. ¿Continuar?`)) return;
    setDownloadingAll(true);
    try {
      await downloadAllClassBackups(classes);
    } finally {
      setDownloadingAll(false);
    }
  };

  const dashboardStats = [
    { label: 'Total de Clases', value: classes.length, icon: BookOpen, color: 'bg-blue-500' },
    { label: 'Total de Alumnos', value: classes.reduce((acc, c) => acc + (c.student_ids?.length || 0), 0), icon: Users, color: 'bg-green-500' },
    { label: 'Tareas Activas', value: stats.active_tasks, icon: ClipboardList, color: 'bg-orange-500' },
    { label: 'Asistencia Promedio', value: `${stats.attendance_percentage}%`, icon: TrendingUp, color: 'bg-purple-500' },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 md:p-8 lg:p-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="dashboard-title">
            Bienvenido, {user.name}
          </h1>
          <p className="text-base text-slate-600">Aquí está el resumen de tu día</p>
          <div className="mt-2">
            <VideoTutorialButton videoUrl="https://youtu.be/TOu7sRtjXUo?si=RjCBuzOxojWbKY6b" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardStats.map((stat, index) => (
            <Card key={index} className="p-6 bg-white border border-slate-100 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-all" data-testid={`stat-card-${index}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Jefe Docentes Notifications Board */}
        {jefeNotifications.length > 0 && (
          <div className="mb-6 space-y-2" data-testid="jefe-notifications-board">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Bell className="w-4 h-4 text-indigo-500" /> Avisos de Jefe de Docentes
            </h3>
            {jefeNotifications.map(n => (
              <Card key={n.id} className={`p-3 border-l-4 ${n.notification_type === 'citatorio' ? 'border-l-rose-500 bg-rose-50' : 'border-l-amber-500 bg-amber-50'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${n.notification_type === 'citatorio' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                      {n.notification_type === 'citatorio' ? 'Citatorio' : 'Aviso'}
                    </span>
                    {n.motivo && <p className="text-sm mt-1"><strong>Motivo:</strong> {n.motivo}</p>}
                    {n.mensaje && <p className="text-sm mt-1">{n.mensaje}</p>}
                    {n.fecha && <p className="text-xs text-slate-600 mt-1">Fecha: {n.fecha} | Hora: {n.hora} | Lugar: {n.lugar}</p>}
                    <p className="text-xs text-slate-400 mt-1">De: {n.created_by_name}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => dismissJefeNotif(n.id)} className="text-slate-500 hover:text-slate-700">Entendido</Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Mis Clases</h2>
            <div className="flex gap-3">
              <Button
                onClick={handleDownloadAllBackups}
                disabled={downloadingAll || classes.length === 0}
                data-testid="download-all-backups-button"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-4 py-2.5 rounded-lg shadow-sm transition-all active:scale-95"
                title="Descargar respaldo de todas las clases (un archivo por clase)"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloadingAll ? 'Descargando…' : 'Respaldo (todas)'}
              </Button>
              <Button
                onClick={() => navigate('/classes')}
                data-testid="manage-classes-button"
                className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2.5 rounded-lg shadow-sm transition-all active:scale-95"
              >
                Gestionar Clases
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-600">Cargando...</div>
          ) : classes.length === 0 ? (
            <Card className="p-12 bg-white border border-slate-100 rounded-xl text-center">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No hay clases todavía</h3>
              <p className="text-slate-600 mb-6">Comienza creando tu primera clase</p>
              <Button
                onClick={() => navigate('/classes?create=1')}
                data-testid="create-first-class-button"
                className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2.5 rounded-lg"
              >
                Crear Primera Clase
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {classes.map((classItem) => (
                <Card
                  key={classItem.id}
                  className="p-6 bg-white border border-slate-100 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md hover:border-orange-200 transition-all cursor-pointer group"
                  data-testid={`class-card-${classItem.id}`}
                  onClick={() => navigate(`/class/${classItem.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-gradient-to-br from-orange-50 to-slate-100 w-12 h-12 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Download Excel button */}
                      <Button
                        onClick={(e) => downloadClassExcel(e, classItem)}
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-2 py-1 h-auto"
                        data-testid={`download-btn-${classItem.id}`}
                        title="Descargar reporte Excel"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Excel
                      </Button>
                      {/* Encuadre button or thumbnail */}
                      {classItem.encuadre_url ? (
                        <div
                          onClick={(e) => handleEncuadreClick(e, classItem)}
                          className="w-10 h-10 rounded-lg overflow-hidden border-2 border-purple-200 hover:border-purple-400 transition-colors cursor-pointer"
                          title="Ver encuadre"
                        >
                          <img src={classItem.encuadre_url} alt="Encuadre" className="w-full h-full object-cover" />
                        </div>
                      ) : classItem.encuadre_manual?.length > 0 ? (
                        <Button
                          onClick={(e) => handleEncuadreClick(e, classItem)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-auto"
                          data-testid={`encuadre-btn-${classItem.id}`}
                          title="Ver/editar encuadre"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Encuadre
                        </Button>
                      ) : (
                        <Button
                          onClick={(e) => handleEncuadreClick(e, classItem)}
                          size="sm"
                          className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 h-auto"
                          data-testid={`encuadre-btn-${classItem.id}`}
                        >
                          <Image className="w-3 h-3 mr-1" />
                          Encuadre
                        </Button>
                      )}
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                        {classItem.group}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-orange-500 transition-colors">{classItem.subject}</h3>
                  <p className="text-sm text-slate-600 mb-4">{classItem.description || 'Sin descripción'}</p>
                  <div className="flex items-center text-sm text-slate-600">
                    <Users className="w-4 h-4 mr-1" />
                    {classItem.student_ids?.length || 0} alumnos
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Encuadre Dialog (choice / manual / photo view) */}
      <Dialog open={encuadreDialog} onOpenChange={setEncuadreDialog}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Encuadre - {selectedClass?.subject}</DialogTitle>
          </DialogHeader>

          {/* CHOICE: subir foto o escribir manual */}
          {encuadreMode === 'choice' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <button
                onClick={handleReplaceEncuadre}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                data-testid="encuadre-choice-photo"
              >
                <Image className="w-8 h-8 text-purple-500" />
                <span className="font-semibold text-slate-800">Subir foto</span>
                <span className="text-xs text-slate-500 text-center">Elige una imagen del encuadre (PNG, JPG, WEBP)</span>
              </button>
              <button
                onClick={chooseManual}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                data-testid="encuadre-choice-manual"
              >
                <FileText className="w-8 h-8 text-purple-500" />
                <span className="font-semibold text-slate-800">Escribir manualmente</span>
                <span className="text-xs text-slate-500 text-center">Agrega criterios con su nombre y peso</span>
              </button>
            </div>
          )}

          {/* MANUAL: agregar criterios */}
          {encuadreMode === 'manual' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={critNombre}
                  onChange={(e) => setCritNombre(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCriterio(); } }}
                  placeholder="Nombre del criterio (ej. Examen)"
                  className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                  data-testid="criterio-nombre-input"
                />
                <input
                  value={critPeso}
                  onChange={(e) => setCritPeso(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCriterio(); } }}
                  placeholder="Peso % (ej. 30)"
                  className="sm:w-36 h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                  data-testid="criterio-peso-input"
                />
                <Button onClick={addCriterio} className="bg-purple-500 hover:bg-purple-600" data-testid="add-criterio-btn">
                  <Plus className="w-4 h-4 mr-1" /> Agregar criterio
                </Button>
              </div>

              {manualCriterios.length > 0 ? (
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="criterios-list">
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                    <span>Criterio</span><span>Peso</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {manualCriterios.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`criterio-row-${idx}`}>
                        <span className="font-medium text-slate-800">{c.nombre}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-purple-700 font-semibold">
                            {c.peso ? (String(c.peso).includes('%') ? c.peso : `${c.peso}%`) : '—'}
                          </span>
                          <button onClick={() => removeCriterio(idx)} className="text-red-500 hover:text-red-700" data-testid={`remove-criterio-${idx}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                    <span>Total</span>
                    {(() => {
                      const total = manualCriterios.reduce((s, c) => s + (parseFloat(String(c.peso).replace('%', '')) || 0), 0);
                      return (
                        <span className={total === 100 ? 'text-green-600' : 'text-red-600'} data-testid="criterios-total">
                          {total}%{total !== 100 ? ' (debe sumar 100%)' : ' ✓'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Aún no has agregado criterios.</p>
              )}

              <div className="flex justify-end gap-2">
                {selectedClass?.encuadre_manual?.length > 0 && (
                  <Button variant="outline" onClick={deleteManualEncuadre} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-manual-encuadre-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                )}
                <Button onClick={saveManualEncuadre} disabled={savingManual} className="bg-purple-500 hover:bg-purple-600" data-testid="save-manual-encuadre-btn">
                  {savingManual ? 'Guardando...' : 'Guardar encuadre'}
                </Button>
              </div>
            </div>
          )}

          {/* VIEW: foto existente */}
          {encuadreMode === 'view' && selectedClass?.encuadre_url && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img
                  src={selectedClass.encuadre_url}
                  alt="Encuadre"
                  className="max-w-full max-h-[60vh] object-contain rounded-lg border border-slate-200"
                />
              </div>
              <div className="flex justify-center gap-3">
                <Button
                  onClick={handleReplaceEncuadre}
                  disabled={uploading}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="replace-encuadre-btn"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {uploading ? 'Subiendo...' : 'Cambiar Encuadre'}
                </Button>
                <Button
                  onClick={handleDeleteEncuadre}
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-encuadre-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Notification Dialog */}
      <Dialog open={adminNotifications.length > 0} onOpenChange={() => {}}>
        <DialogContent className="bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Megaphone className="w-5 h-5" />
              Aviso del Administrador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-slate-700">{adminNotifications[currentAdminNotifIndex]?.mensaje}</p>
            </div>
            <p className="text-xs text-slate-400">
              Enviado por: {adminNotifications[currentAdminNotifIndex]?.created_by_name} | {adminNotifications[currentAdminNotifIndex]?.created_at ? new Date(adminNotifications[currentAdminNotifIndex].created_at).toLocaleDateString('es-MX') : ''}
            </p>
            {adminNotifications.length > 1 && (
              <p className="text-xs text-slate-500">Notificación {currentAdminNotifIndex + 1} de {adminNotifications.length}</p>
            )}
            <Button onClick={markAdminNotifRead} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="mark-admin-notif-read">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TeacherDashboard;
